import { ReactNode } from "react";
import { ChevronLeft, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/contexts/LanguageContext";
import type { BreadcrumbItem as BreadcrumbItemType } from "./OverlayPage";

interface OverlayHeaderProps {
  title: string | ReactNode;
  breadcrumbs: BreadcrumbItemType[];
  entityRef?: string;
  onClose: () => void;
}

/**
 * OverlayHeader - Standard Side Sheet header (content-driven height).
 * 
 * Canonical Standard Side Sheet Baseline:
 * - Title: LEFT-aligned (after optional back arrow)
 * - Close X: RIGHT-aligned (always present)
 * - Back arrow: Only when navigation depth exists (breadcrumbs > 1)
 * - Height: Content-driven with fixed padding (py-4 = 16dp top/bottom)
 * - Horizontal padding: px-6 = 24dp
 */
export function OverlayHeader({
  title,
  breadcrumbs,
  entityRef,
  onClose,
}: OverlayHeaderProps) {
  const isMobile = useIsMobile();
  const { t } = useLanguage();

  // Show back arrow only when there's navigation depth (more than 1 breadcrumb)
  const hasNavigationDepth = breadcrumbs.length > 1;
  const backLabel = breadcrumbs[0]?.label || t("common.back");

  return (
    <header className="sticky top-0 bg-muted/50 backdrop-blur-sm border-b border-border flex items-center justify-between px-4 sm:px-6 py-4 shrink-0 sm:rounded-tl-lg">
      {/* Left: Back (if depth) + Title + Entity Ref */}
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {hasNavigationDepth && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="gap-1.5 -ml-2 text-muted-foreground hover:text-foreground shrink-0"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className={isMobile ? "sr-only" : "hidden sm:inline"}>{backLabel}</span>
          </Button>
        )}
        
        <div className="flex items-center gap-2 min-w-0">
          {typeof title === "string" ? (
            <h1 className="text-base font-semibold truncate">{title}</h1>
          ) : (
            title
          )}
          {entityRef && (
            <span className="text-sm text-muted-foreground shrink-0">
              {entityRef}
            </span>
          )}
        </div>
      </div>

      {/* Right: Close X (always present) */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onClose}
        className="shrink-0 -mr-2 text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" />
        <span className="sr-only">{t("common.close")}</span>
      </Button>
    </header>
  );
}
